import { Component } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * ErrorBoundary — catches any render exception inside its children and shows
 * a rugged fallback UI instead of a white screen. Wraps the authenticated
 * router outlet so a bug on any single page (Swing, Insights, Owner Center)
 * cannot brick the whole portal for the crew.
 *
 * Design decision: no auto-reload loop. We render an explicit "Reload" button
 * so the supervisor gets to decide when to refresh. Includes a "Go home"
 * shortcut back to `/` because 90% of the time the failed page isn't Swing.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[Pit Link ErrorBoundary]", error, info);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      const stack = this.state.info?.componentStack || "";
      return (
        <div className="min-h-[calc(100vh-160px)] flex items-center justify-center p-6" data-testid="app-error-boundary">
          <div className="panel-heavy w-full max-w-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: "rgba(220,38,38,0.14)", color: "#ff8a8a", border: "1px solid rgba(220,38,38,0.35)" }}
              >
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted">Rendering error</div>
                <div className="font-display font-black text-xl tracking-tight">Something crashed on this page.</div>
              </div>
            </div>
            <p className="text-sm text-muted mb-4">
              We caught the exception before it could blank your screen. Your data on the production backend is safe — nothing was written on this render pass.
            </p>
            <div
              className="rounded-lg p-3 text-[11px] font-mono-tight whitespace-pre-wrap break-all max-h-48 overflow-auto mb-4"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", color: "#ff9b9b" }}
              data-testid="app-error-message"
            >
              {msg}
              {stack ? `\n\n${stack.trim().split("\n").slice(0, 6).join("\n")}` : ""}
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <a href="/" className="btn btn-ghost btn-sm" data-testid="app-error-home">
                <Home className="w-4 h-4" /> Go home
              </a>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn btn-primary btn-sm"
                data-testid="app-error-reload"
              >
                <RefreshCw className="w-4 h-4" /> Reload
              </button>
              <button
                type="button"
                onClick={this.reset}
                className="btn btn-ghost btn-sm"
                data-testid="app-error-retry"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
